import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, Inbox } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statuses = ["new", "contacted", "scheduled", "archived"];

const badgeCls = {
  new: "bg-gold-500/15 text-gold-700 border-gold-500/40",
  contacted: "bg-forest-500/10 text-forest-700 border-forest-500/30",
  scheduled: "bg-forest-800 text-cream-50 border-forest-800",
  archived: "bg-cream-200 text-sage border-cream-300",
};

const AdminDashboard = () => {
  usePageMeta("Consultation Requests | Admin", "Admin dashboard.");
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const load = useCallback(async () => {
    const res = await fetch(`${API}/consultations`, { credentials: "include" });
    if (res.status === 401) {
      navigate("/admin/login");
      return;
    }
    const data = await res.json();
    setItems(data.consultations);
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    await fetch(`${API}/consultations/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status }),
    });
    load();
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/admin/login");
  };

  const visible = (items || []).filter((c) => {
    const matchStatus = filter === "all" || c.status === filter;
    const q = query.toLowerCase();
    const matchQuery =
      !q || c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.message.toLowerCase().includes(q);
    return matchStatus && matchQuery;
  });

  return (
    <div className="min-h-screen bg-cream-50" data-testid="admin-dashboard">
      <div className="bg-forest-950 py-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
          <div>
            <p className="eyebrow mb-1">Admin</p>
            <h1 className="font-serif text-2xl sm:text-3xl text-cream-50" data-testid="admin-dashboard-title">Consultation Requests</h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 rounded-full border border-cream-50/20 text-cream-50/80 text-xs font-semibold px-5 py-2.5 hover:border-gold-500 hover:text-gold-400 transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between mb-8">
          <div className="flex flex-wrap gap-2" data-testid="admin-status-filters">
            {["all", ...statuses].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`admin-filter-${s}`}
                className={`rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                  filter === s ? "bg-forest-800 text-cream-50" : "bg-cream-100 text-sage hover:bg-cream-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, message…"
            data-testid="admin-search-input"
            className="rounded-full border border-forest-800/15 bg-white/70 px-5 py-2.5 text-sm w-full sm:w-72 focus:outline-none focus:ring-2 focus:ring-gold-500/50"
          />
        </div>

        {items === null ? (
          <div className="flex justify-center py-20" data-testid="admin-loading">
            <Loader2 className="animate-spin text-forest-800" size={32} />
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-20 text-sage" data-testid="admin-empty-state">
            <Inbox className="mx-auto mb-4 text-gold-500" size={40} strokeWidth={1.5} />
            <p className="font-serif text-2xl text-forest-800 mb-2">No requests yet</p>
            <p className="text-sm">New consultation requests will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-requests-list">
            {visible.map((c) => (
              <div key={c.id} className="rounded-2xl border border-forest-800/10 bg-white/70 p-6 sm:p-7" data-testid={`admin-request-${c.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="font-serif text-xl text-forest-800" data-testid={`admin-request-name-${c.id}`}>{c.name}</p>
                    <p className="text-xs text-sage mt-1">
                      {new Date(c.created_at).toLocaleString()} · {c.therapy_type}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] ${badgeCls[c.status] || badgeCls.new}`} data-testid={`admin-request-status-${c.id}`}>
                      {c.status}
                    </span>
                    <select
                      value={c.status}
                      onChange={(e) => setStatus(c.id, e.target.value)}
                      data-testid={`admin-status-select-${c.id}`}
                      className="rounded-lg border border-forest-800/15 bg-cream-50 px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-gold-500/50"
                    >
                      {statuses.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3 text-sm text-ink/80 mb-4">
                  <p><span className="text-xs uppercase tracking-[0.15em] text-sage block">Email</span>{c.email}</p>
                  <p><span className="text-xs uppercase tracking-[0.15em] text-sage block">Phone</span>{c.phone || "—"}</p>
                  <p><span className="text-xs uppercase tracking-[0.15em] text-sage block">Preferred contact</span>{c.preferred_contact || "—"}</p>
                </div>
                <p className="text-sm text-sage leading-relaxed border-t border-forest-800/10 pt-4" data-testid={`admin-request-message-${c.id}`}>{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
